import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getData, putData, deleteData, serverURL } from "../../services/FetchNodeservices";

/* ---------------------------------
   RESPONSIVE MODAL FOR INTERVIEWS
   ---------------------------------- */
function Modal({ open, title, children, onClose, width = "max-w-md" }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className={`bg-white w-full ${width} rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]`}>
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-black text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------
   ALL 17 DETAILED STATUSES
   ---------------------------------- */
const STATUSES = [
  "Feedback Pending",
  "Screening",
  "Shortlisted",
  "Interview L1",
  "Interview L2",
  "Face to Face",
  "Selected",
  "Offered",
  "Joining Pending",
  "Joined",
  "Rejected",
  "No Show",
  "Hold",
  "Backout",
  "Duplicate",
];

/* ---------------------------------
   CEIPAL ATS PIPELINE STAGES GROUPING
   ---------------------------------- */
const PIPELINE_GROUPS = [
  { name: "All", statuses: STATUSES },
  { name: "Feedback Pending", statuses: ["Feedback Pending"] },
  { name: "Screening", statuses: ["Screening"] },
  { name: "Shortlisted", statuses: ["Shortlisted"] },
  { name: "Interviews", statuses: ["Interview L1", "Interview L2", "Face to Face"] },
  { name: "Selected / Offered", statuses: ["Selected", "Offered", "Joining Pending"] },
  { name: "Joined", statuses: ["Joined"] },
  { name: "Rejected / Other", statuses: ["Rejected", "No Show", "Hold", "Backout", "Duplicate"] },
];

export default function JobCandidatesPipeline() {
  const { reqId } = useParams();
  const navigate = useNavigate();

  // Candidates & Search/Filters States
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState([]); // Selected candidate IDs for export

  // Left/Right selection states
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // "resume" or "details"

  const DEFAULT_FIELDS = [
    { key: "f_name", label: "First Name" },
    { key: "l_name", label: "Last Name" },
    { key: "email_id", label: "Email ID" },
    { key: "phone_number", label: "Phone Number" },
    { key: "current_company", label: "Current Company" },
    { key: "designation", label: "Designation" },
    { key: "experience", label: "Experience" },
    { key: "current_ctc", label: "Current CTC" },
    { key: "expected_ctc", label: "Expected CTC" },
    { key: "qualification", label: "Highest Qualification" },
    { key: "hometown", label: "Location / Hometown" },
    { key: "added_by", label: "Source / Added By" },
    { key: "uploaded_on", label: "Added Date", readOnly: true },
  ];

  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const [fields, setFields] = useState(() => {
    try {
      const stored = localStorage.getItem("jobcandidate_fields");
      return stored ? JSON.parse(stored) : DEFAULT_FIELDS;
    } catch (e) {
      return DEFAULT_FIELDS;
    }
  });
  const [dbColumns, setDbColumns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddColumnDropdown, setShowAddColumnDropdown] = useState(false);

  const fetchDbColumns = async () => {
    try {
      const res = await getData("candidate/columns");
      if (res.status) {
        setDbColumns(res.data);
      }
    } catch (err) {
      console.error("fetchDbColumns error:", err);
    }
  };

  useEffect(() => {
    fetchDbColumns();
  }, []);

  const EXCLUDED_COLUMNS = [
    "candidate_id",
    "req_id",
    "status",
    "status_color",
    "added_by_id",
    "updated_on",
    "resume",
    "interview_date",
    "interview_time"
  ];
  const availableColumns = dbColumns.filter(
    (col) => !fields.some((f) => f.key === col) && !EXCLUDED_COLUMNS.includes(col)
  );

  const handleAddColumn = (col) => {
    const newField = {
      key: col,
      label: formatLabel(col),
    };
    const updated = [...fields, newField];
    setFields(updated);
    localStorage.setItem("jobcandidate_fields", JSON.stringify(updated));
    setShowAddColumnDropdown(false);
  };

  const handleRemoveColumn = (key) => {
    const updated = fields.filter((f) => f.key !== key);
    setFields(updated);
    localStorage.setItem("jobcandidate_fields", JSON.stringify(updated));
  };

  const handleFieldChange = (key, val) => {
    setSelectedCandidateDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: val,
      };
    });
  };

  const handleSaveChanges = async () => {
    if (!selectedCandidateId || !selectedCandidateDetails) return;
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((field) => {
        if (!field.readOnly) {
          payload[field.key] = selectedCandidateDetails[field.key];
        }
      });

      const res = await putData(`candidate/${selectedCandidateId}`, payload);
      if (res.status) {
        setCandidates((prev) =>
          prev.map((c) => {
            if (c.id === selectedCandidateId) {
              return {
                ...c,
                full_name: `${selectedCandidateDetails.f_name || ""} ${selectedCandidateDetails.l_name || ""}`.trim(),
                phone_number: selectedCandidateDetails.phone_number,
                experience: selectedCandidateDetails.experience,
                designation: selectedCandidateDetails.designation,
                current_company: selectedCandidateDetails.current_company,
              };
            }
            return c;
          })
        );
        alert("Profile details updated successfully!");
      } else {
        alert("Failed to save changes");
      }
    } catch (err) {
      console.error("Save details error:", err);
      alert("Error saving candidate details");
    } finally {
      setSaving(false);
    }
  };

  // Interview modal states
  const [interviewModal, setInterviewModal] = useState(false);
  const [interviewData, setInterviewData] = useState({
    candidateId: null,
    status: "",
    date: "",
    time: "",
  });

  const getResumeUrl = (resume) => {
    if (!resume) return "";
    if (resume.startsWith("http://") || resume.startsWith("https://")) {
      return resume;
    }
    return `${serverURL}/uploads/Candidate-Resume/${resume}`;
  };

  /* ---------------------------------
     FETCH CANDIDATES LIST
     ---------------------------------- */
  const fetchCandidates = useCallback(async () => {
    if (!reqId) return;
    setLoading(true);

    try {
      const res = await getData(`candidate/jobcandidates/${reqId}`);
      if (res.status) {
        const flat = res.data.map((c) => ({
          ...c,
          status: c.status || "Feedback Pending",
        }));
        setCandidates(flat);
      }
    } catch (err) {
      console.error("fetchCandidates error:", err);
    } finally {
      setLoading(false);
    }
  }, [reqId]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  /* ---------------------------------
     LOAD SINGLE CANDIDATE DETAILS
     ---------------------------------- */
  const handleSelectCandidate = async (candidateId) => {
    setSelectedCandidateId(candidateId);
    setSelectedCandidateDetails(null);
    setLoadingDetails(true);
    try {
      const res = await getData(`candidate/details/${candidateId}`);
      if (res.status) {
        setSelectedCandidateDetails(res.data);
      }
    } catch (err) {
      console.error("load candidate details error:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  /* ---------------------------------
     STATUS TRANSITIONS
     ---------------------------------- */
  const handleStatusChange = async (candidateId, newStatus) => {
    const interviewStages = ["Interview L1", "Interview L2", "Face to Face"];

    if (interviewStages.includes(newStatus)) {
      setInterviewData({
        candidateId,
        status: newStatus,
        date: "",
        time: "",
      });
      setInterviewModal(true);
      return;
    }

    try {
      const res = await putData(`candidate/${candidateId}`, { status: newStatus });
      if (res.status) {
        // Update local candidates list state
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status: newStatus } : c))
        );
        // Update loaded profile state if currently active
        setSelectedCandidateDetails((prev) =>
          prev ? { ...prev, status: newStatus } : prev
        );
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const handleInterviewSave = async () => {
    const { candidateId, status, date, time } = interviewData;

    if (!date || !time) {
      alert("Please select date & time");
      return;
    }

    try {
      const res = await putData(`candidate/${candidateId}`, {
        status,
        interview_date: date,
        interview_time: time,
      });

      if (res.status) {
        setInterviewModal(false);
        // Update local state
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status } : c))
        );
        setSelectedCandidateDetails((prev) =>
          prev ? { ...prev, status } : prev
        );
        fetchCandidates();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save interview scheduling");
    }
  };

  /* ---------------------------------
     DELETE CANDIDATE
     ---------------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this candidate from the pipeline?")) return;
    try {
      const res = await deleteData(`candidate/${id}`);
      if (res.status) {
        // Remove from list and details
        if (selectedCandidateId === id) {
          setSelectedCandidateId(null);
          setSelectedCandidateDetails(null);
        }
        setCandidates((prev) => prev.filter((c) => c.id !== id));
        setSelected((prev) => prev.filter((x) => x !== id));
      } else {
        alert("Delete failed");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting candidate");
    }
  };

  /* ---------------------------------
     EXPORT EXCEL
     ---------------------------------- */
  const exportExcel = () => {
    const rows = selected.length
      ? candidates.filter((c) => selected.includes(c.id))
      : candidates;

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pipeline Candidates");
    XLSX.writeFile(wb, `Requisition_${reqId}_Candidates.xlsx`);
  };

  /* ---------------------------------
     PIPELINE STAGE CALCULATOR
     ---------------------------------- */
  const getGroupCount = (groupName) => {
    if (groupName === "All") return candidates.length;
    const group = PIPELINE_GROUPS.find((g) => g.name === groupName);
    if (!group) return 0;
    return candidates.filter((c) => group.statuses.includes(c.status)).length;
  };

  /* ---------------------------------
     DYNAMIC SEARCH & GROUP FILTERING
     ---------------------------------- */
  const filteredCandidates = candidates.filter((c) => {
    // 1. Group status filter
    if (activeGroup !== "All") {
      const group = PIPELINE_GROUPS.find((g) => g.name === activeGroup);
      if (!group || !group.statuses.includes(c.status)) return false;
    }

    // 2. Search box query filter
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const name = `${c.f_name || ""} ${c.l_name || ""} ${c.candidate_name || ""}`.toLowerCase();
      const email = (c.email_id || "").toLowerCase();
      const phone = (c.phone_number || "").toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q);
    }

    return true;
  });

  return (
    <div className="p-4 sm:p-6 bg-slate-50/50 min-h-screen">
      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 mt-20">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5">
            Candidate Pipeline – {reqId}
          </h2>
          <p className="text-xs font-bold text-gray-400 mt-1">
            Browse and transition candidate profiles inside job requirement pipelines
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={exportExcel}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-750 text-white bg-indigo-600 rounded-2xl text-xs font-bold shadow-lg shadow-indigo-100 transition duration-200"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Excel
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-250 hover:bg-gray-50 text-gray-600 rounded-2xl text-xs font-bold border-gray-200 transition duration-200"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>
      </div>

      {/* HORIZONTAL CHEVRON PIPELINE PROGRESS STAGES BAR */}
      <div className="flex flex-wrap items-center bg-white border border-gray-150/70 rounded-3xl p-1.5 gap-1.5 mb-6 shadow-sm overflow-x-auto">
        {PIPELINE_GROUPS.map((group) => {
          const isActive = activeGroup === group.name;
          const count = getGroupCount(group.name);
          return (
            <button
              key={group.name}
              onClick={() => {
                setActiveGroup(group.name);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-black rounded-2xl transition-all duration-200 whitespace-nowrap ${
                isActive
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`}
            >
              <span>{group.name}</span>
              <span
                className={`px-1.5 py-0.5 text-[9px] font-black rounded-lg ${
                  isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* SPLIT WORKSPACE INTERFACE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-280px)] min-h-[550px]">
        
        {/* LEFT COMPONENT - PANEL LIST OF CANDIDATES (4 Columns) */}
        <div className="lg:col-span-5 xl:col-span-4 bg-white border border-gray-150/70 rounded-3xl p-4 flex flex-col h-full shadow-sm overflow-hidden">
          {/* Quick Search */}
          <div className="space-y-3 mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-gray-150/70 rounded-2xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:bg-white transition duration-200"
              />
              <svg className="absolute left-3.5 top-3 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <label className="flex items-center gap-2 text-xs font-bold text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filteredCandidates.length > 0 && filteredCandidates.every((c) => selected.includes(c.id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = [...selected];
                      filteredCandidates.forEach((c) => {
                        if (!newSelected.includes(c.id)) newSelected.push(c.id);
                      });
                      setSelected(newSelected);
                    } else {
                      const filteredIds = filteredCandidates.map((c) => c.id);
                      setSelected(selected.filter((id) => !filteredIds.includes(id)));
                    }
                  }}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Select Page ({filteredCandidates.length})</span>
              </label>
              {selected.length > 0 && (
                <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-lg border border-indigo-100/50">
                  {selected.length} Selected
                </span>
              )}
            </div>
          </div>

          {/* Scrolling Candidate Card Stack */}
          <div className="flex-1 overflow-y-auto pr-1 space-y-3">
            {filteredCandidates.map((c) => {
              const isSelected = selectedCandidateId === c.id;
              const displayStatus = c.status || "Feedback Pending";
              return (
                <div
                  key={c.id}
                  onClick={() => handleSelectCandidate(c.id)}
                  className={`flex items-start gap-3 p-3.5 rounded-2xl border transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? "bg-indigo-50/40 border-indigo-250/80 border-indigo-500 shadow-sm"
                      : "bg-white border-gray-150/70 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() =>
                      setSelected((p) =>
                        p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]
                      )
                    }
                    className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-extrabold text-xs text-gray-900 truncate">
                        {c.full_name || c.candidate_name || `${c.f_name || ""} ${c.l_name || ""}`}
                      </h4>
                      <span className={`px-2 py-0.5 text-[9px] font-black rounded-lg uppercase tracking-wider ${
                        displayStatus === "Selected" || displayStatus === "Joined"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : displayStatus === "Rejected" || displayStatus === "Duplicate"
                          ? "bg-rose-50 text-rose-700 border border-rose-100"
                          : "bg-amber-50 text-amber-700 border border-amber-100"
                      }`}>
                        {displayStatus}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 font-semibold truncate mt-0.5">
                      {c.designation || c.current_designation || "No Designation"}
                    </p>
                    {c.current_company && (
                      <p className="text-[10px] text-gray-400 font-semibold truncate">
                        {c.current_company}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400 font-bold">
                      <span>Exp: {c.experience || "0"} Years</span>
                      {c.phone_number && <span>• {c.phone_number}</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredCandidates.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 font-bold text-xs">
                <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2m16 0l-3.586-3.586a2 2 0 00-2.828 0L16 11m-8 3H4" />
                </svg>
                No candidates in this stage
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COMPONENT - DETAIL VIEWER PANEL (8 Columns) */}
        <div className="lg:col-span-7 xl:col-span-8 bg-white border border-gray-150/70 rounded-3xl flex flex-col h-full shadow-sm overflow-hidden">
          {selectedCandidateId ? (
            <>
              {/* Detailed panel header */}
              <div className="px-6 py-4 border-b border-gray-150/60 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-black text-gray-900 truncate">
                    {selectedCandidateDetails ? `${selectedCandidateDetails.f_name || ""} ${selectedCandidateDetails.l_name || ""}` : "Loading..."}
                  </h3>
                  <p className="text-xs font-bold text-gray-500 truncate mt-0.5 flex items-center gap-2">
                    <span>{selectedCandidateDetails?.email_id}</span>
                    {selectedCandidateDetails?.phone_number && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{selectedCandidateDetails.phone_number}</span>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Status Dropdown Transition */}
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Change Stage:</span>
                    <select
                      value={selectedCandidateDetails?.status || ""}
                      onChange={(e) => handleStatusChange(selectedCandidateId, e.target.value)}
                      className="h-8 px-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800 focus:outline-none focus:border-indigo-500 transition duration-200 cursor-pointer shadow-sm"
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Generate Summary */}
                  <button
                    onClick={() => window.open(`/candidate-summary/${selectedCandidateId}`, "_blank")}
                    className="h-8 px-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition duration-150 flex items-center justify-center gap-1.5 shadow-sm shadow-indigo-100 cursor-pointer animate-fadeIn"
                    title="Generate Summary Sheet"
                  >
                    Generate Summary
                  </button>

                  {/* Generate Bajaj Summary */}
                  <button
                    onClick={() => window.open(`/bajaj-summary-sheet/${selectedCandidateId}`, "_blank")}
                    className="h-8 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition duration-150 flex items-center justify-center gap-1.5 shadow-sm shadow-emerald-100 cursor-pointer animate-fadeIn"
                    title="Generate Bajaj Summary Sheet"
                  >
                    Bajaj Summary Sheet
                  </button>

                  {/* Delete Candidate */}
                  <button
                    onClick={() => handleDelete(selectedCandidateId)}
                    className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition duration-150 border border-gray-200 hover:border-rose-100 shadow-sm"
                    title="Delete from pipeline"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tab Selector Buttons */}
              <div className="border-b border-gray-150/60 flex px-6 bg-gray-50/20">
               
                <button
                  onClick={() => setActiveTab("details")}
                  className={`py-3.5 px-4 text-xs font-black border-b-2 transition duration-200 ${
                    activeTab === "details"
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Profile Details
                </button>
                 <button
                  onClick={() => setActiveTab("resume")}
                  className={`py-3.5 px-4 text-xs font-black border-b-2 transition duration-200 ${
                    activeTab === "resume"
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Resume Preview
                </button>
              </div>

              {/* Tab panel contents */}
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/20">
                {loadingDetails ? (
                  <div className="flex flex-col items-center justify-center h-full text-xs text-gray-400 font-bold py-16">
                    <svg className="w-8 h-8 text-indigo-500 animate-spin mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading candidate profile...
                  </div>
                ) : activeTab === "resume" ? (
                  selectedCandidateDetails?.resume ? (
                    getResumeUrl(selectedCandidateDetails.resume).toLowerCase().endsWith(".pdf") ? (
                      <iframe
                        src={getResumeUrl(selectedCandidateDetails.resume)}
                        className="w-full h-full border border-gray-200 rounded-3xl shadow-inner min-h-[450px]"
                        title="Resume Preview"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full p-8 border-2 border-dashed border-gray-200 rounded-3xl bg-white text-center min-h-[350px]">
                        <svg className="w-14 h-14 text-indigo-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h4 className="text-xs font-black text-gray-800">Preview not supported for non-PDF files</h4>
                        <p className="text-[10px] text-gray-400 mt-1 max-w-xs">
                          This resume is stored as a Microsoft Word or other formatting type. Please download to view.
                        </p>
                        <a
                          href={getResumeUrl(selectedCandidateDetails.resume)}
                          download
                          className="mt-5 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-755 text-white rounded-2xl text-[11px] font-bold transition duration-200 shadow-lg shadow-indigo-100"
                        >
                          Download Resume File
                        </a>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400 font-bold min-h-[350px]">
                      No resume file is attached to this candidate.
                    </div>
                  )
                ) : (
                  /* Profile details Container */
                  <div className="space-y-6">
                    {/* Actions Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white px-6 py-4 border border-gray-150/70 rounded-3xl shadow-sm gap-3">
                      <h4 className="text-xs font-black text-gray-900 uppercase tracking-wider">Candidate Profile Information</h4>
                      <div className="flex items-center gap-3 relative">
                        {/* Add Column Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowAddColumnDropdown(!showAddColumnDropdown)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-250 hover:bg-gray-55 text-gray-600 rounded-xl text-xs font-bold transition duration-200 border-gray-200 shadow-sm"
                          >
                            <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Column
                          </button>

                          {showAddColumnDropdown && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowAddColumnDropdown(false)}
                              />
                              <div className="absolute right-0 mt-2 w-56 max-h-60 overflow-y-auto bg-white border border-gray-150 rounded-2xl shadow-xl z-20 p-2 space-y-1">
                                {availableColumns.length > 0 ? (
                                  availableColumns.map((col) => (
                                    <button
                                      key={col}
                                      onClick={() => handleAddColumn(col)}
                                      className="w-full text-left px-3 py-2 text-xs font-bold text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl transition duration-150"
                                    >
                                      {formatLabel(col)}
                                    </button>
                                  ))
                                ) : (
                                  <div className="text-[10px] text-center text-gray-400 py-3 font-semibold">
                                    No additional columns
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Save Changes Button */}
                        <button
                          onClick={handleSaveChanges}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-705 text-white rounded-xl text-xs font-bold transition duration-200 disabled:opacity-50 shadow-md shadow-indigo-100/50"
                        >
                          {saving ? (
                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          Save Changes
                        </button>
                      </div>
                    </div>

                    {/* Grid of Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 border border-gray-150/70 rounded-3xl shadow-sm">
                      {fields.map((field) => {
                        const isDefault = DEFAULT_FIELDS.some((df) => df.key === field.key);
                        return (
                          <div key={field.key} className="border-b border-gray-100 pb-3 group relative">
                            <div className="flex items-center justify-between">
                              <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider">
                                {field.label}
                              </label>
                              {!isDefault && (
                                <button
                                  onClick={() => handleRemoveColumn(field.key)}
                                  className="text-gray-300 hover:text-rose-500 transition duration-150 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                  title="Remove Column"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {field.readOnly ? (
                              <p className="text-xs font-bold text-gray-800 mt-2 bg-slate-50/50 px-3 py-2 rounded-xl border border-gray-100">
                                {field.key === "uploaded_on" && selectedCandidateDetails?.[field.key]
                                  ? new Date(selectedCandidateDetails[field.key]).toLocaleString()
                                  : selectedCandidateDetails?.[field.key] || "—"}
                              </p>
                            ) : (
                              <input
                                type="text"
                                value={selectedCandidateDetails?.[field.key] ?? ""}
                                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                className="w-full mt-1.5 px-3 py-2 bg-slate-50 border border-gray-150/60 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:bg-white transition duration-200"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Empty selection state */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50/20">
              <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-4 mb-4">
                <svg className="w-10 h-10 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              <h4 className="text-sm font-black text-gray-800">No Candidate Selected</h4>
              <p className="text-xs text-gray-400 mt-1.5 max-w-sm font-semibold">
                Click on a candidate name in the left panel to display their interactive resume preview, profile info, and change their pipeline stage.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* SCHEDULE INTERVIEW MODAL */}
      <Modal
        open={interviewModal}
        title="Schedule Pipeline Stage Interview"
        onClose={() => setInterviewModal(false)}
        width="max-w-sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
              Interview Date
            </label>
            <input
              type="date"
              className="w-full border border-gray-200 px-3.5 py-2.5 rounded-2xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200"
              value={interviewData.date}
              onChange={(e) =>
                setInterviewData({ ...interviewData, date: e.target.value })
              }
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">
              Interview Time
            </label>
            <input
              type="time"
              className="w-full border border-gray-200 px-3.5 py-2.5 rounded-2xl text-xs font-semibold focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200"
              value={interviewData.time}
              onChange={(e) =>
                setInterviewData({ ...interviewData, time: e.target.value })
              }
            />
          </div>

          <div className="flex justify-end gap-2.5 pt-4">
            <button
              onClick={() => setInterviewModal(false)}
              className="px-4 py-2.5 text-xs font-bold border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-2xl transition duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleInterviewSave}
              className="px-5 py-2.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-755 text-white bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-100 transition duration-200"
            >
              Schedule
            </button>
          </div>
        </div>
      </Modal>

      {/* OVERALL SPINNER LOADING */}
      {loading && (
        <div className="fixed inset-0 bg-white/70 flex flex-col items-center justify-center z-50">
          <svg className="w-10 h-10 text-indigo-600 animate-spin mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-xs font-black text-gray-500 uppercase tracking-wider">Syncing candidate pipeline...</span>
        </div>
      )}
    </div>
  );
}